import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentAcuantValidation } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import {
    BadResolution,
    BlurDetection,
    DCUIThemingTextContentAcuantValidationTypes,
    DetectionValidationHeader,
    GlareDetection,
    LivenessDetection
} from "@enums/dcui-theming-text-content-acuant-validation-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { DCUIThemingTextContentAcuantValidationSectionValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { TextareaFormGroup } from "../../../../../common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingTextContentValidationGenericSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    displayText: string;
    acuantValidationType: DCUIThemingTextContentAcuantValidationTypes;
    validationErrors: DCUIThemingTextContentAcuantValidationSectionValidationErrors;
    updateDCUIContentAcuantValidationFieldValue: (key: keyof DCUIContentAcuantValidation, value: string) => void;
};

export const DCUIThemingTextContentValidationGenericAcuantSection = (props: DCUIThemingTextContentValidationGenericSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        displayText,
        acuantValidationType,
        validationErrors,
        updateDCUIContentAcuantValidationFieldValue: updateDCUIContentAcuantValidationFieldValue
    } = props;

    let detectionValidationHeaderText: keyof DCUIContentAcuantValidation;
    let detectionSuccessMessage: keyof DCUIContentAcuantValidation;
    let detectionErrorMessage: keyof DCUIContentAcuantValidation;

    switch (acuantValidationType) {
        case DCUIThemingTextContentAcuantValidationTypes.BAD_RESOLUTION:
            detectionValidationHeaderText = `${DetectionValidationHeader.LOW_DPI}DetectionValidationHeader`;
            detectionSuccessMessage = `${BadResolution.VALID}Message`;
            detectionErrorMessage = `${BadResolution.INVALID}Message`;
            break;
        case DCUIThemingTextContentAcuantValidationTypes.BLUR_DETECTION:
            detectionValidationHeaderText = `${DetectionValidationHeader.LOW_SHARPNESS}DetectionValidationHeader`;
            detectionSuccessMessage = `${BlurDetection.VALID}Message`;
            detectionErrorMessage = `${BlurDetection.INVALID}Message`;
            break;
        case DCUIThemingTextContentAcuantValidationTypes.GLARE_DETECTION:
            detectionValidationHeaderText = `${DetectionValidationHeader.GLARE}DetectionValidationHeader`;
            detectionSuccessMessage = `${GlareDetection.VALID}Message`;
            detectionErrorMessage = `${GlareDetection.INVALID}Message`;
            break;
        case DCUIThemingTextContentAcuantValidationTypes.LIVENESS_DETECTION:
            detectionValidationHeaderText = `${DetectionValidationHeader.LIVENESS}DetectionValidationHeader`;
            detectionSuccessMessage = `${LivenessDetection.VALID}Message`;
            detectionErrorMessage = `${LivenessDetection.INVALID}Message`;
            break;
    }

    const handleDetectionSubheadingChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentAcuantValidationFieldValue(detectionValidationHeaderText, event.target.value);
    };

    const handleResetDetectionSubheading = () => {
        updateDCUIContentAcuantValidationFieldValue(detectionValidationHeaderText, inheritedDCUIContent.acuant.validation[detectionValidationHeaderText]);
    };

    const handleDetectionSuccessMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentAcuantValidationFieldValue(detectionSuccessMessage, event.target.value);
    };

    const handleResetDetectionSuccessMessage = () => {
        updateDCUIContentAcuantValidationFieldValue(detectionSuccessMessage, inheritedDCUIContent.acuant.validation[detectionSuccessMessage]);
    };

    const handleDetectionErrorMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentAcuantValidationFieldValue(detectionErrorMessage, event.target.value);
    };

    const handleResetDetectionErrorMessage = () => {
        updateDCUIContentAcuantValidationFieldValue(detectionErrorMessage, inheritedDCUIContent.acuant.validation[detectionErrorMessage]);
    };

    const validationGenericResources = resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.textContent.sections.validation.sections.common;

    return (
        <div>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.validation[detectionValidationHeaderText]}
                currentThemingPropertyValue={dcuiContent.acuant.validation[detectionValidationHeaderText]}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={`document-capture-text-content-acuant-validation-${acuantValidationType}-detection-subheading-input`}
                    automationId={`document-capture-text-content-acuant-validation-${acuantValidationType}-detection-subheading-input`}
                    label={validationGenericResources.inputs.subheading.label(displayText)}
                    ariaLabel={validationGenericResources.inputs.subheading.label(displayText)}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleDetectionSubheadingChange}
                    value={dcuiContent.acuant.validation[detectionValidationHeaderText] || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={validationGenericResources.inputs.subheading.tooltip(displayText)}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetDetectionSubheading}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.detectionValidationHeaderTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.validation[detectionSuccessMessage]}
                currentThemingPropertyValue={dcuiContent.acuant.validation[detectionSuccessMessage]}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={`document-capture-text-content-acuant-validation-${acuantValidationType}-detection-success-message-input`}
                    automationId={`document-capture-text-content-acuant-validation-${acuantValidationType}-detection-success-message-input`}
                    label={validationGenericResources.inputs.successMessage.label(displayText)}
                    ariaLabel={validationGenericResources.inputs.successMessage.label(displayText)}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleDetectionSuccessMessageChange}
                    value={dcuiContent.acuant.validation[detectionSuccessMessage] || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={validationGenericResources.inputs.successMessage.tooltip(displayText)}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetDetectionSuccessMessage}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.detectionSuccessMessageError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.validation[detectionErrorMessage]}
                currentThemingPropertyValue={dcuiContent.acuant.validation[detectionErrorMessage]}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={`document-capture-text-content-acuant-validation-${acuantValidationType}-detection-error-message-input`}
                    automationId={`document-capture-text-content-acuant-validation-${acuantValidationType}-detection-error-message-input`}
                    label={validationGenericResources.inputs.errorMessage.label(displayText)}
                    ariaLabel={validationGenericResources.inputs.errorMessage.label(displayText)}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleDetectionErrorMessageChange}
                    value={dcuiContent.acuant.validation[detectionErrorMessage] || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={validationGenericResources.inputs.errorMessage.tooltip(displayText)}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetDetectionErrorMessage}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.detectionErrorMessageError}
        </div>
    );
};